import { Disclosure } from "@headlessui/react";
import { MinusSmallIcon, PlusSmallIcon } from "@heroicons/react/24/outline";

const faqs = [
  {
    question: "How long will it take for the app to provide analytics?",
    answer:
      "We need one week's worth of data to start providing you with the analytics. We update the dashboard every Monday of the week.",
  },
  {
    question: "How to change my active plan?",
    answer:
      "Revisit the app by going to the Shopify app section in the admin page (cf-app-testing.myshopify.com/admin/apps) and click on CausalFunnel DataScience app. You will land on the main page with a “Change Plan” button. Click on this button and follow instructions on the page to change your plan.",
  },
  {
    question: "How to enable persona prediction?",
    answer:
      "To enable persona prediction and dashboard analytics corresponding to that visit the online theme and go to customize theme, then click on theme settings option and enable CausalFunnel DataScience app so that we can start tracking personas. Once enabled on the theme page, revisit our app from the admin page (cf-app-testing.myshopify.com/admin/apps). Once there, check the settings tab in the dashboard and the “persona prediction” should say “Enabled”.",
  },
  {
    question: "How to check details of my current activated plan?",
    answer:
      "To check details of your current plan, re-visit the app from Shopify admin/apps page. The first page will provide you with the information of your current plan.",
  },
  {
    question: "Will this app change my store's UI?",
    answer:
      "No, we will not change your store's UI. We don't make any kind of injections in your code. Our persona feature, if enabled, will just show a nudge which will float on top of your UI. You can see how it works on our listing page demo store.",
  },
  {
    question: "How to see the buying intent and persona nudges in action?",
    answer:
      "To see the buying intent and persona prediction in action, use the demo store link to navigate to the demo store. If the user browses some of the products and switches to a different tab without closing the current tab/browser and gets back to the tab (user inactive), CausalFunnel's AI on this demo store will judge you to have abandoned the website and will provide a popup to get you to buy. A recorded demo is also available here: buying intent and persona nudges demo. \n Note: The AI algorithm deployed on this demo store is a demo code that is a simpler version of our AI.",
  },
  {
    question: "How to enable persona nudges?",
    answer:
      "Once you buy the Enterprise Plan, please email us to set up your nudges offer, discount percentage, and coupon IDs for deployment on the store. Our team will get back in touch within 2-3 working days to set up the persona-based nudges for you.",
  },
  {
    question: "How to use the dashboard to improve ad campaigns?",
    answer:
      "You can launch multiple ad campaigns and use the 'marketing performance' tab in the dashboard to track and compare the visitors, add to cart, and final purchase numbers for different campaigns. In the Enterprise version of the plan, our team will also help you A/B test your different campaigns.",
  },
];


export default function Help() {
  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center py-8">
      <div className="w-full max-w-5xl px-6 mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl mb-6 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Find answers to common questions about CausalFunnel DataScience app
          </p>
        </div>

        {/* FAQ Section */}
        <div className="space-y-4 mb-12">
          {faqs.map((faq, index) => (
            <Disclosure as="div" key={faq.question}>
              {({ open }) => (
                <div className={`bg-white rounded-2xl shadow-sm border border-gray-200 transition-all duration-200 ${open ? 'ring-2 ring-blue-400 shadow-md' : 'hover:shadow-md'}`}>
                  <dt>
                    <Disclosure.Button className="flex w-full items-start justify-between text-left p-6 group">
                      <div className="flex items-start gap-4 flex-1">
                        <span className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-sm">
                          {index + 1}
                        </span>
                        <span className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors pr-4">
                          {faq.question}
                        </span>
                      </div>
                      <span className="flex-shrink-0 ml-6 flex h-8 w-8 items-center justify-center rounded-full bg-blue-50 group-hover:bg-blue-100 transition-colors">
                        {open ? (
                          <MinusSmallIcon
                            className="h-5 w-5 text-blue-600"
                            aria-hidden="true"
                          />
                        ) : (
                          <PlusSmallIcon
                            className="h-5 w-5 text-blue-600"
                            aria-hidden="true"
                          />
                        )}
                      </span>
                    </Disclosure.Button>
                  </dt>
                  <Disclosure.Panel as="dd" className="px-6 pb-6">
                    <div className="pl-12 pr-14 pt-2">
                      <p className="text-base leading-7 text-gray-600 border-l-4 border-blue-200 pl-4">
                        {faq.answer}
                      </p>
                    </div>
                  </Disclosure.Panel>
                </div>
              )}
            </Disclosure>
          ))}
        </div>

        {/* Contact Section */}
        <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl shadow-xl p-8 text-center">
          <div className="max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-3">
              Still have questions?
            </h3>
            <p className="text-blue-50 text-lg mb-6">
              We're here to help! Our support team is ready to assist you.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="mailto:hi@causalfunnel.com"
                className="inline-flex items-center gap-2 bg-white text-blue-600 px-6 py-3 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-200 hover:scale-105 shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                hi@causalfunnel.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
